import React, { useState, useEffect } from 'react';
import {
  AppBar, Toolbar, Box, Button, IconButton, Drawer, List, ListItem,
  Container, Menu, MenuItem, Fade, ListItemButton, Divider,
  useTheme, useMediaQuery, Collapse, ListItemText, Fab, Avatar,
  ListItemIcon, Typography, Tooltip,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PersonIcon from '@mui/icons-material/Person';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import AppsOutlinedIcon from '@mui/icons-material/AppsOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import logo1 from '../assets/logo1.png';

// Helper: detectar sesión activa sin importar authApi (evita dependencia circular)
const _isLoggedNav = () => !!localStorage.getItem('ecolab_token');
const _isAdminNav  = () => {
  try {
    const u = JSON.parse(localStorage.getItem('ecolab_user') || '{}');
    return (u.roles||[]).some(r => ['SuperAdmin','AdminConfederacion','AdminFederacion','AdminCooperativa'].includes(r));
  } catch { return false; }
};

const navItems = [
  { label: 'Inicio', path: '/' },
  {
    label: 'Nosotros', path: '/nosotros',
    children: [
      { label: 'Misión y Visión', path: '/mision-vision' },
      { label: 'Principios', path: '/principios' },
      { label: 'Valores', path: '/valores' },
      { label: 'Políticas de Calidad', path: '/politicas-calidad' },
      { label: 'Políticas de Privacidad', path: '/politicas-privacidad' },
    ],
  },
  {
    label: 'Bienes y Servicios', path: '/bienes-servicios',
    children: [
      { label: 'Escuelas Agroindustriales', path: '/escuelas-agroindustriales' },
      { label: 'Redes Empresariales', path: '/redes-empresariales' },
      { label: 'Banco de Proyectos', path: '/banco-proyectos' },
      { label: 'Mercado Asegurado', path: '/mercado-asegurado' },
      { label: 'Banco de Innovación', path: '/banco-innovacion' },
      { label: 'Banco de Emprendimiento', path: '/banco-emprendimiento' },
    ],
  },
  { label: 'Nuestra Red', path: '/nuestra-red' },
  { label: 'Afiliación', path: '/afiliacion' },
  { label: 'Contacto', path: '/contacto' },
];

// ── Scroll-to-top FAB ─────────────────────────────────────
export function ScrollToTopFab() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const fn = () => setVisible(window.scrollY > 400);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  return (
    <Fab
      size="medium"
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      sx={{
        position: 'fixed', bottom: 32, right: 32, zIndex: 1200,
        background: 'linear-gradient(135deg, #2e7d32, #43a047)',
        color: '#fff',
        boxShadow: '0 6px 24px rgba(46,125,50,0.35)',
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? 'all' : 'none',
        transform: visible ? 'translateY(0)' : 'translateY(20px)',
        transition: 'all 0.35s cubic-bezier(0.4,0,0.2,1)',
        '&:hover': {
          background: 'linear-gradient(135deg, #1b5e20, #2e7d32)',
          transform: visible ? 'translateY(-4px)' : 'translateY(20px)',
          boxShadow: '0 10px 30px rgba(46,125,50,0.45)',
        },
      }}
    >
      <KeyboardArrowUpIcon />
    </Fab>
  );
}

// ── Main Navbar ────────────────────────────────────────────
export default function Navbar() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const location = useLocation();

  const isHome = location.pathname === '/';
  const [scrolled, setScrolled] = useState(!isHome);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openMobileMenus, setOpenMobileMenus] = useState({});
  const [anchorEls, setAnchorEls] = useState({});

  // Leer sesión SINCRÓNICAMENTE en el estado inicial para evitar el parpadeo
  const _readSession = () => {
    const token = localStorage.getItem('ecolab_token');
    if (!token) return { logged: false, admin: false, user: null };
    try {
      const u = JSON.parse(localStorage.getItem('ecolab_user') || '{}');
      const adminRoles = ['SuperAdmin','AdminConfederacion','AdminFederacion','AdminCooperativa'];
      return {
        logged: true,
        admin: (u.roles || []).some(r => adminRoles.includes(r)),
        user: u,
      };
    } catch { return { logged: true, admin: false, user: null }; }
  };
  const _s = _readSession();
  const [isLogged,    setIsLogged]    = useState(_s.logged);
  const [isAdminUser, setIsAdminUser] = useState(_s.admin);
  const [sessionUser, setSessionUser] = useState(_s.user);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Solo escuchar cambios posteriores (logout/login desde otra pestaña o componente)
    const checkSession = () => {
      const { logged, admin, user } = _readSession();
      setIsLogged(logged);
      setIsAdminUser(admin);
      setSessionUser(user);
    };
    window.addEventListener('storage', checkSession);
    // Escuchar evento personalizado de logout dentro de la misma pestaña
    window.addEventListener('ecolab_session_change', checkSession);
    return () => {
      window.removeEventListener('storage', checkSession);
      window.removeEventListener('ecolab_session_change', checkSession);
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    setScrolled(!isHome);
  }, [location.pathname, isHome]);

  useEffect(() => {
    const handleScroll = () => {
      if (!isHome) { setScrolled(true); return; }
      setScrolled(window.scrollY > 60);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHome]);

  const handleMenuOpen = (e, label) => setAnchorEls(p => ({ ...p, [label]: e.currentTarget }));
  const handleMenuClose = (label) => setAnchorEls(p => ({ ...p, [label]: null }));
  const toggleMobileMenu = (label) => setOpenMobileMenus(p => ({ ...p, [label]: !p[label] }));

  // colour tokens
  const txtColor = scrolled ? '#1a3a1e' : '#ffffff';
  const hoverBg = scrolled ? 'rgba(46,125,50,0.08)' : 'rgba(255,255,255,0.15)';
  const txtShadow = scrolled ? 'none' : '0 1px 6px rgba(0,0,0,0.4)';

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: scrolled ? 'rgba(255,255,255,0.90)' : 'transparent',
          backdropFilter: scrolled ? 'blur(14px)' : 'none',
          boxShadow: scrolled ? '0 2px 20px rgba(0,0,0,0.08)' : 'none',
          borderBottom: scrolled ? '1px solid rgba(46,125,50,0.10)' : 'none',
          backgroundImage: 'none',
          transition: 'all 0.4s ease',
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ minHeight: { xs: 64, md: 72 }, justifyContent: 'space-between', mt: '10px' }}>

            {/* Logo */}
            <Box
              component={Link}
              to="/"
              sx={{ display: 'flex', alignItems: 'center', gap: 1.2, textDecoration: 'none' }}
            >
              <Box
                component="img"
                src={logo1}
                alt="FENAGRO logo"
                sx={{
                  height: { xs: 64, md: 78 },
                  width: 'auto',
                  objectFit: 'contain',
                  display: 'block',
                  flexShrink: 0,
                  filter: !scrolled
                    ? 'drop-shadow(0 0 10px rgba(255,255,255,0.55)) drop-shadow(0 3px 12px rgba(0,0,0,0.45)) brightness(1.08)'
                    : 'drop-shadow(0 2px 6px rgba(46,125,50,0.40)) drop-shadow(0 0 12px rgba(46,125,50,0.20))',
                  transition: 'all 0.4s ease',
                }}
              />
              <Box sx={{
                fontSize: '0.6rem', letterSpacing: '0.16em',
                color: scrolled ? '#4a7c59' : 'rgba(255,255,255,0.85)',
                textTransform: 'uppercase',
                fontFamily: "'Outfit', sans-serif", fontWeight: 600,
                textShadow: txtShadow,
                transition: 'all 0.4s ease',
                lineHeight: 1.4,
              }}>
                APL<br />Red Empresarial
              </Box>
            </Box>

            {/* Desktop nav */}
            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                {navItems.map((item) => (
                  <Box key={item.label}>
                    {item.children ? (
                      <>
                        <Button
                          onClick={(e) => handleMenuOpen(e, item.label)}
                          endIcon={
                            <ExpandMoreIcon sx={{
                              fontSize: '14px !important', transition: 'transform 0.25s',
                              transform: Boolean(anchorEls[item.label]) ? 'rotate(180deg)' : 'none',
                            }} />
                          }
                          sx={{
                            color: txtColor, fontSize: '0.8rem', letterSpacing: '0.03em',
                            fontWeight: 500, px: 1.5, borderRadius: 2,
                            textShadow: txtShadow, transition: 'all 0.3s ease',
                            '&:hover': { background: hoverBg, color: scrolled ? '#2e7d32' : '#fff' },
                          }}
                        >
                          {item.label}
                        </Button>
                        <Menu
                          anchorEl={anchorEls[item.label]}
                          open={Boolean(anchorEls[item.label])}
                          onClose={() => handleMenuClose(item.label)}
                          TransitionComponent={Fade}
                          PaperProps={{
                            sx: {
                              mt: 1.5, minWidth: 230, borderRadius: 3,
                              background: 'rgba(255,255,255,0.97)',
                              backdropFilter: 'blur(20px)',
                              border: '1px solid rgba(46,125,50,0.10)',
                              boxShadow: '0 20px 60px rgba(0,0,0,0.10)',
                            },
                          }}
                        >
                          {item.children.map((child) => (
                            <MenuItem
                              key={child.path}
                              component={Link}
                              to={child.path}
                              onClick={() => handleMenuClose(item.label)}
                              sx={{
                                color: '#2d4a2f', fontSize: '0.85rem', py: 1.3,
                                fontFamily: "'Outfit', sans-serif", fontWeight: 400,
                                transition: 'all 0.2s ease',
                                '&:hover': { background: 'rgba(46,125,50,0.07)', color: '#2e7d32', pl: 3 },
                              }}
                            >
                              {child.label}
                            </MenuItem>
                          ))}
                        </Menu>
                      </>
                    ) : (
                      <Button
                        component={Link}
                        to={item.path}
                        sx={{
                          color: txtColor, fontSize: '0.8rem', letterSpacing: '0.03em',
                          fontWeight: 500, px: 1.5, borderRadius: 2,
                          textShadow: txtShadow, transition: 'all 0.3s ease',
                          borderBottom: location.pathname === item.path
                            ? `2px solid ${scrolled ? '#2e7d32' : '#fff'}`
                            : '2px solid transparent',
                          '&:hover': { background: hoverBg, color: scrolled ? '#2e7d32' : '#fff' },
                        }}
                      >
                        {item.label}
                      </Button>
                    )}
                  </Box>
                ))}

                {/* Botones Ingresar/Afiliarme o Avatar Dropdown */}
                {!isLogged ? (
                  <>
                    {/* Botón Login */}
                    <Button
                      component={Link}
                      to="/login"
                      startIcon={<PersonIcon sx={{ fontSize: 17 }} />}
                      sx={{
                        ml: 1.5,
                        color: scrolled ? '#2e7d32' : 'rgba(255,255,255,0.9)',
                        fontSize: '0.76rem', fontWeight: 600,
                        borderRadius: 3, px: 1.8, py: 0.9,
                        border: '1.5px solid',
                        borderColor: scrolled ? 'rgba(46,125,50,0.4)' : 'rgba(255,255,255,0.4)',
                        background: scrolled ? 'rgba(46,125,50,0.06)' : 'rgba(255,255,255,0.1)',
                        backdropFilter: 'blur(4px)',
                        transition: 'all 0.3s ease',
                        textShadow: 'none',
                        '&:hover': {
                          background: scrolled ? 'rgba(46,125,50,0.12)' : 'rgba(255,255,255,0.2)',
                          borderColor: scrolled ? '#2e7d32' : '#fff',
                        },
                      }}
                    >
                      Ingresar
                    </Button>

                    {/* Botón Alianzas (establecimientos comerciales aliados) */}
                    {(() => {
                      const activo = location.pathname.startsWith('/aliarse');
                      return (
                        <Button
                          variant={activo ? 'contained' : 'outlined'}
                          component={Link}
                          to="/aliarse"
                          sx={{
                            ml: 1,
                            background: activo ? 'linear-gradient(135deg, #2e7d32, #43a047)' : 'transparent',
                            borderColor: activo ? 'transparent' : (scrolled ? '#2e7d32' : 'rgba(255,255,255,0.65)'),
                            color: activo ? '#fff' : (scrolled ? '#2e7d32' : '#fff'),
                            fontSize: '0.78rem', letterSpacing: '0.06em',
                            fontWeight: 700, borderWidth: 2, borderRadius: 3,
                            px: 2.5, py: 0.9,
                            textShadow: (!activo && !scrolled) ? txtShadow : 'none',
                            transition: 'all 0.4s ease',
                            '&:hover': {
                              background: activo
                                ? 'linear-gradient(135deg, #1b5e20, #2e7d32)'
                                : (scrolled ? 'rgba(46,125,50,0.06)' : 'rgba(255,255,255,0.18)'),
                              borderColor: activo ? 'transparent' : (scrolled ? '#2e7d32' : '#fff'),
                              transform: 'translateY(-1px)',
                              boxShadow: activo ? '0 6px 20px rgba(46,125,50,0.3)' : 'none',
                            },
                          }}
                        >
                          Alianzas
                        </Button>
                      );
                    })()}

                    {/* Botón Afiliarme */}
                    {(() => {
                      const activo = location.pathname.startsWith('/afiliacion');
                      return (
                        <Button
                          variant={activo ? 'contained' : 'outlined'}
                          component={Link}
                          to="/afiliacion"
                          sx={{
                            ml: 1,
                            background: activo ? 'linear-gradient(135deg, #2e7d32, #43a047)' : 'transparent',
                            borderColor: activo ? 'transparent' : (scrolled ? '#2e7d32' : 'rgba(255,255,255,0.65)'),
                            color: activo ? '#fff' : (scrolled ? '#2e7d32' : '#fff'),
                            fontSize: '0.78rem', letterSpacing: '0.06em',
                            fontWeight: 700, borderWidth: 2, borderRadius: 3,
                            px: 2.5, py: 0.9,
                            textShadow: (!activo && !scrolled) ? txtShadow : 'none',
                            transition: 'all 0.4s ease',
                            '&:hover': {
                              background: activo
                                ? 'linear-gradient(135deg, #1b5e20, #2e7d32)'
                                : (scrolled ? 'rgba(46,125,50,0.06)' : 'rgba(255,255,255,0.18)'),
                              borderColor: activo ? 'transparent' : (scrolled ? '#2e7d32' : '#fff'),
                              transform: 'translateY(-1px)',
                              boxShadow: activo ? '0 6px 20px rgba(46,125,50,0.3)' : 'none',
                            },
                          }}
                        >
                          Afiliarme
                        </Button>
                      );
                    })()}
                  </>
                ) : (
                  <UserAvatarMenu
                    sessionUser={sessionUser}
                    isAdminUser={isAdminUser}
                    scrolled={scrolled}
                    anchor={userMenuAnchor}
                    setAnchor={setUserMenuAnchor}
                    navigate={navigate}
                  />
                )}
              </Box>
            )}

            {/* Mobile icon */}
            {isMobile && (
              <IconButton
                onClick={() => setDrawerOpen(true)}
                sx={{
                  color: txtColor,
                  background: scrolled ? 'transparent' : 'rgba(255,255,255,0.15)',
                  border: scrolled ? 'none' : '1px solid rgba(255,255,255,0.3)',
                  backdropFilter: 'blur(4px)',
                  transition: 'all 0.3s ease',
                }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: 300, background: 'rgba(255,255,255,0.97)', backdropFilter: 'blur(20px)' } }}
      >
        <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ fontFamily: "'Playfair Display', serif", fontWeight: 900, fontSize: '1.3rem', color: '#1a3a1e' }}>
            FENAGRO
          </Box>
          <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: '#4a7c59' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ borderColor: 'rgba(46,125,50,0.15)' }} />
        <List sx={{ px: 1, pt: 1 }}>
          {navItems.map((item) => (
            <Box key={item.label}>
              {item.children ? (
                <>
                  <ListItemButton
                    onClick={() => toggleMobileMenu(item.label)}
                    sx={{ borderRadius: 2, py: 1.2, '&:hover': { background: 'rgba(46,125,50,0.07)' } }}
                  >
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ sx: { color: '#1a3a1e', fontFamily: "'Outfit', sans-serif", fontSize: '0.92rem', fontWeight: 500 } }}
                    />
                    {openMobileMenus[item.label]
                      ? <ExpandLessIcon sx={{ color: '#2e7d32', fontSize: 18 }} />
                      : <ExpandMoreIcon sx={{ color: '#9ab9a0', fontSize: 18 }} />}
                  </ListItemButton>
                  <Collapse in={openMobileMenus[item.label]}>
                    <List disablePadding sx={{ pl: 2 }}>
                      {item.children.map((child) => (
                        <ListItem key={child.path} disablePadding>
                          <ListItemButton
                            component={Link} to={child.path}
                            onClick={() => setDrawerOpen(false)}
                            sx={{ borderRadius: 2, py: 0.9, '&:hover': { background: 'rgba(46,125,50,0.07)' } }}
                          >
                            <ListItemText
                              primary={child.label}
                              primaryTypographyProps={{ sx: { color: '#4a7c59', fontFamily: "'Outfit', sans-serif", fontSize: '0.84rem' } }}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                </>
              ) : (
                <ListItem disablePadding>
                  <ListItemButton
                    component={Link} to={item.path}
                    onClick={() => setDrawerOpen(false)}
                    sx={{ borderRadius: 2, py: 1.2, '&:hover': { background: 'rgba(46,125,50,0.07)' } }}
                  >
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ sx: { color: '#1a3a1e', fontFamily: "'Outfit', sans-serif", fontSize: '0.92rem', fontWeight: 500 } }}
                    />
                  </ListItemButton>
                </ListItem>
              )}
            </Box>
          ))}
        </List>
        <Box sx={{ p: 3, mt: 'auto', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {!isLogged ? (
            <>
              <Button
                variant="outlined" fullWidth component={Link}
                to="/login" onClick={() => setDrawerOpen(false)}
                startIcon={<PersonIcon/>}
                sx={{
                  color: '#2e7d32', borderColor: '#2e7d32', fontWeight: 700,
                  borderRadius: 3, py: 1.2,
                  '&:hover': { background: 'rgba(46,125,50,0.06)' },
                }}
              >
                Ingresar
              </Button>
              <Button
                variant="contained" fullWidth component={Link}
                to="/afiliacion" onClick={() => setDrawerOpen(false)}
                sx={{
                  background: 'linear-gradient(135deg, #2e7d32, #43a047)',
                  color: '#fff', fontWeight: 700, borderRadius: 3, py: 1.4,
                  '&:hover': { background: 'linear-gradient(135deg, #1b5e20, #2e7d32)' },
                }}
              >
                Afiliarme Ahora
              </Button>
              <Button
                variant="outlined" fullWidth component={Link}
                to="/aliarse" onClick={() => setDrawerOpen(false)}
                sx={{
                  borderColor: '#2e7d32', color: '#2e7d32', fontWeight: 700,
                  borderRadius: 3, py: 1.2,
                  '&:hover': { background: 'rgba(46,125,50,0.06)' },
                }}
              >
                Alianzas
              </Button>
            </>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.2, borderRadius: 2, background: 'rgba(46,125,50,0.06)' }}>
                <Avatar
                  src={sessionUser?.fotoUrl
                    || (sessionUser?.imagen && !sessionUser.imagen.startsWith('gs://') ? sessionUser.imagen : undefined)}
                  sx={{ width: 42, height: 42, background: 'linear-gradient(135deg, #2e7d32, #43a047)', color: '#fff', fontWeight: 700 }}
                >
                  {(sessionUser?.nombre?.[0] || '') + (sessionUser?.apellido?.[0] || '')}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: '#1a3a1e', lineHeight: 1.2 }} noWrap>
                    {sessionUser?.nombre} {sessionUser?.apellido}
                  </Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: '#4a7c59' }} noWrap>
                    {sessionUser?.email}
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="outlined" fullWidth component={Link}
                to="/mi-portal" onClick={() => setDrawerOpen(false)}
                startIcon={<AppsOutlinedIcon/>}
                sx={{ color: '#2e7d32', borderColor: '#2e7d32', fontWeight: 600, borderRadius: 3, py: 1.1 }}
              >
                Dashboard
              </Button>
              <Button
                variant="outlined" fullWidth component={Link}
                to="/mi-perfil" onClick={() => setDrawerOpen(false)}
                startIcon={<AccountCircleOutlinedIcon/>}
                sx={{ color: '#2e7d32', borderColor: '#2e7d32', fontWeight: 600, borderRadius: 3, py: 1.1 }}
              >
                Mi perfil
              </Button>
              {isAdminUser && (
                <Button
                  variant="outlined" fullWidth component={Link}
                  to="/admin/dashboard" onClick={() => setDrawerOpen(false)}
                  startIcon={<DashboardOutlinedIcon/>}
                  sx={{ color: '#2e7d32', borderColor: '#2e7d32', fontWeight: 600, borderRadius: 3, py: 1.1 }}
                >
                  Panel admin
                </Button>
              )}
              <Button
                variant="contained" fullWidth
                onClick={() => {
                  localStorage.removeItem('ecolab_token');
                  localStorage.removeItem('ecolab_user');
                  window.dispatchEvent(new Event('ecolab_session_change'));
                  setDrawerOpen(false);
                  window.location.href = '/';
                }}
                startIcon={<LogoutOutlinedIcon/>}
                sx={{
                  background: '#b71c1c', color: '#fff',
                  fontWeight: 700, borderRadius: 3, py: 1.2,
                  '&:hover': { background: '#7f0000' },
                }}
              >
                Cerrar sesión
              </Button>
            </>
          )}
        </Box>
      </Drawer>

      <ScrollToTopFab />
    </>
  );
}

// ── User Avatar Menu (desktop) — card horizontal ───────────────────
function UserAvatarMenu({ sessionUser, isAdminUser, scrolled, anchor, setAnchor, navigate }) {
  const initials = ((sessionUser?.nombre?.[0] || '') + (sessionUser?.apellido?.[0] || '')).toUpperCase();
  const fotoSrc = sessionUser?.fotoUrl
    || (sessionUser?.imagen && !sessionUser.imagen.startsWith('gs://') ? sessionUser.imagen : undefined);
  const nombreCompleto = `${sessionUser?.nombre || ''} ${sessionUser?.apellido || ''}`.trim() || 'Usuario';
  const rolesTexto = (sessionUser?.roles || []).join(' · ') || 'Sin rol';

  const handleLogout = (e) => {
    e.stopPropagation();
    localStorage.removeItem('ecolab_token');
    localStorage.removeItem('ecolab_user');
    window.dispatchEvent(new Event('ecolab_session_change'));
    setAnchor(null);
    // Usar window.location.href en lugar de navigate() para forzar reload
    // completo. Sin reload, las variables _token/_user en memoria de
    // authApi.js conservan los valores anteriores e isLogged() sigue
    // devolviendo true, causando re-redirect a /mi-portal al ir a /login.
    window.location.href = '/';
  };

  return (
    <>
      {/* Card: avatar + nombre + roles + botón salir */}
      <Box
        sx={{
          ml: 1.5,
          display: 'flex', alignItems: 'center', gap: 1.2,
          py: 0.6, px: 1.2, pr: 0.4,
          background: 'linear-gradient(135deg, #1a4d2e, #2e7d32)',
          borderRadius: 999,
          minWidth: 200, maxWidth: 280,
          boxShadow: scrolled ? '0 2px 8px rgba(46,125,50,0.25)' : '0 2px 12px rgba(0,0,0,0.25)',
          transition: 'all 0.25s ease',
          '&:hover .nav-user-info': { background: 'rgba(255,255,255,0.06)' },
        }}
      >
        {/* Área info clickable — abre dropdown */}
        <Box
          className="nav-user-info"
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1.2,
            flex: 1, minWidth: 0,
            cursor: 'pointer',
            py: 0.3, px: 0.6, borderRadius: 999,
            transition: 'background 0.2s ease',
          }}
          aria-label="Menú de usuario"
          role="button"
        >
          <Avatar
            src={fotoSrc}
            sx={{
              width: 34, height: 34,
              background: 'rgba(255,255,255,0.18)',
              color: '#fff', fontWeight: 700, fontSize: '0.82rem',
              flexShrink: 0,
              border: '1.5px solid rgba(255,255,255,0.3)',
            }}
          >
            {initials || <PersonIcon sx={{ fontSize: 20 }} />}
          </Avatar>
          <Box sx={{ minWidth: 0, lineHeight: 1.15 }}>
            <Typography
              sx={{
                color: '#fff', fontWeight: 700, fontSize: '0.82rem',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}
            >
              {nombreCompleto}
            </Typography>
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.72)', fontSize: '0.66rem',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                letterSpacing: '0.02em',
              }}
            >
              {rolesTexto}
            </Typography>
          </Box>
        </Box>

        {/* Botón salir — acción directa */}
        <Tooltip title="Cerrar sesión" arrow>
          <IconButton
            size="small"
            onClick={handleLogout}
            sx={{
              color: 'rgba(255,255,255,0.85)',
              background: 'rgba(255,255,255,0.08)',
              width: 32, height: 32, flexShrink: 0,
              border: '1px solid rgba(255,255,255,0.15)',
              transition: 'all 0.2s ease',
              '&:hover': {
                background: 'rgba(255,255,255,0.2)',
                color: '#fff',
                borderColor: 'rgba(255,255,255,0.4)',
              },
            }}
            aria-label="Cerrar sesión"
          >
            <LogoutOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Dropdown — Mi perfil + Panel admin */}
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        TransitionComponent={Fade}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            mt: 1, minWidth: 220, borderRadius: 2.5,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            border: '1px solid rgba(46,125,50,0.10)',
          },
        }}
      >
        <MenuItem
          onClick={() => { setAnchor(null); navigate('/mi-portal'); }}
          sx={{ py: 1.2, px: 2 }}
        >
          <ListItemIcon><AppsOutlinedIcon sx={{ color: '#2e7d32' }} /></ListItemIcon>
          <ListItemText
            primary="Dashboard"
            primaryTypographyProps={{ sx: { fontSize: '0.86rem', color: '#1a3a1e' } }}
          />
        </MenuItem>

        <MenuItem
          onClick={() => { setAnchor(null); navigate('/mi-perfil'); }}
          sx={{ py: 1.2, px: 2 }}
        >
          <ListItemIcon><AccountCircleOutlinedIcon sx={{ color: '#2e7d32' }} /></ListItemIcon>
          <ListItemText
            primary="Mi perfil"
            primaryTypographyProps={{ sx: { fontSize: '0.86rem', color: '#1a3a1e' } }}
          />
        </MenuItem>

        {isAdminUser && (
          <MenuItem
            onClick={() => { setAnchor(null); navigate('/admin/dashboard'); }}
            sx={{ py: 1.2, px: 2 }}
          >
            <ListItemIcon><DashboardOutlinedIcon sx={{ color: '#2e7d32' }} /></ListItemIcon>
            <ListItemText
              primary="Panel administrativo"
              primaryTypographyProps={{ sx: { fontSize: '0.86rem', color: '#1a3a1e' } }}
            />
          </MenuItem>
        )}
      </Menu>
    </>
  );
}
